import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Talkr } from 'talkr';
import { HashRouter, Routes, Route } from 'react-router-dom';
import defaultLang from './en.json';
import ViewportSizes from './components/viewport';
import firebaseConfig from './firebaseConfig';
import replaceUrl from './utils/scripts/replaceUrl';
import { initOneTrustTracking } from './utils/scripts/initOneTrustTracking';
import { initAmplitude } from './analytics/amplitude';
import { initTikTokPixel } from './analytics/initTikTokPixel';
import { initGoogleAds } from './analytics/initGoogleAds';
import App from './App';
import { useAppStore } from './store';
import getParameterFromURL from './utils/scripts/getParameterFromURL';
import * as configs from './survey/configs';
import { startScreens } from './survey/startScreens';

import './index.scss';

window.dataLayer = window.dataLayer || [];
window.defaultLocales = defaultLang;
// Ранний preload первого экрана (LCP). Пока качается/исполняется main.js,
// параллельно прогреваем lazy-чанк стартовой страницы и тянем hero-картинку.
// Конфиг → первый шаг резолвится так же, как в useResolvedConfig.
{
  const startCfg = getParameterFromURL('config');
  const startMap = configs as unknown as Record<string, Array<{ name: string }>>;
  const startResolved = startCfg && startCfg in configs ? startMap[startCfg] : startMap.customV18;
  const startScreen = startResolved?.[0]?.name ? startScreens[startResolved[0].name] : undefined;

  if (startScreen) {
    startScreen.load();

    const isDark = getParameterFromURL('darkTheme') === 'true';
    const hero = isDark && startScreen.heroDark ? startScreen.heroDark : startScreen.hero;

    if (hero) {
      const heroLink = document.createElement('link');
      heroLink.rel = 'preload';
      heroLink.as = 'image';
      heroLink.type = 'image/avif';
      heroLink.href = hero;
      heroLink.setAttribute('fetchpriority', 'high');
      document.head.appendChild(heroLink);
    }
  }
}

initAmplitude();
initTikTokPixel();
initGoogleAds();
firebaseConfig();
replaceUrl();
initOneTrustTracking();

let userLang = window.navigator.language.slice(0, 2);

type LocalisationsProps = { children: React.ReactNode };

function Localisations({ children }: LocalisationsProps) {
  const forceLang = useAppStore((state) => state.forceLang);
  // Стартуем с дефолтной (English) локалью из бандла — рендер первого экрана
  // не ждёт сетевой загрузки локали. Реальная локаль подменяется когда подгрузится.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [userLocale, setUserLocale] = useState<any>(defaultLang);
  const allowedLanguages = ['en', 'ru', 'es', 'fr', 'de', 'pt', 'it', 'tr', 'pl', 'uk'];

  useEffect(() => {
    const selectedLang = allowedLanguages.includes(userLang) ? userLang : 'en';

    fetch(`./locales/${forceLang || selectedLang}.json`)
      .then((response) => response.json())
      .then((data) => {
        setUserLocale(data);
        window.locales = data;
        window.userLang = selectedLang;
        const htmlEl = document.querySelector('html');
        if (htmlEl) htmlEl.lang = selectedLang;
      })
      .catch(() => {
        userLang = 'en';
        window.userLang = 'en';
        window.locales = defaultLang;
        setUserLocale(defaultLang);
      });
  }, []);

  return (
    <Talkr
      languages={{ en: defaultLang, [userLang]: userLocale }}
      defaultLanguage="en"
      detectBrowserLanguage
    >
      {children}
    </Talkr>
  );
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <HashRouter>
    <React.StrictMode>
      <Localisations>
        <Routes>
          <Route path="/" element={<App />} />
        </Routes>
        <ViewportSizes />
      </Localisations>
    </React.StrictMode>
  </HashRouter>,
);
